import React, { useEffect, useState } from 'react';
import { GlassCard } from './GlassCard';
import { Brain, LineChart, Users, Heart, Shield } from 'lucide-react';
interface CardRingProps {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onManualInteraction: () => void;
  onOpenModule: (moduleId: string) => void;
}
interface Photon {
  id: string;
  targetIndex: number;
  startTime: number;
}
export function CardRing({
  activeIndex,
  setActiveIndex,
  onManualInteraction,
  onOpenModule
}: CardRingProps) {
  const [photons, setPhotons] = useState<Photon[]>([]);
  // Cards with new agent role structure
  const cards = [{
    id: 'safety',
    title: '平安校园',
    agentRole: '易教·守护',
    subtitle: '多模态行为识别，构建零容忍防欺凌安全防线',
    icon: Shield,
    color: 'cyan' as const
  }, {
    id: 'classroom',
    title: '课堂洞察',
    agentRole: '易教·慧眼',
    subtitle: '捕捉师生互动数据，量化教学质量指标',
    icon: LineChart,
    color: 'cyan' as const
  }, {
    id: 'growth',
    title: '成长关怀',
    agentRole: '易教·关爱',
    subtitle: '测评干预全流程，守护学生身心健康',
    icon: Heart,
    color: 'pink' as const
  }, {
    id: 'knowledge',
    title: '智能批改',
    agentRole: '易教·助教',
    subtitle: '作业智能批改，精准定位薄弱知识点',
    icon: Brain,
    color: 'cyan' as const
  }, {
    id: 'ai-tutor',
    title: '智慧伴学',
    agentRole: '易教·伴学',
    subtitle: '大模型驱动，全天候苏格拉底式智能助教',
    icon: Users,
    color: 'purple' as const
  }];
  // Photon emission effect
  useEffect(() => {
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * 5);
      const newPhoton: Photon = {
        id: `photon-${Date.now()}-${randomIndex}`,
        targetIndex: randomIndex,
        startTime: Date.now()
      };
      setPhotons(prev => [...prev, newPhoton]);
      setTimeout(() => {
        setPhotons(prev => prev.filter(p => p.id !== newPhoton.id));
      }, 1500);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  return <div className="relative w-full max-w-7xl mx-auto h-[600px] flex flex-col items-center justify-center" data-id="element-37">
      {/* Central Brain Core */}
      <div className="relative mb-16 flex flex-col items-center" data-id="element-38">
        <div className="relative brain-core" data-id="element-39">
          <div className="absolute inset-0 rounded-full bg-[#0EA5E9]/20 blur-2xl scale-150" data-id="element-40"></div>
          <div className="absolute inset-0 rounded-full bg-[#0EA5E9]/10 blur-3xl scale-[2]" data-id="element-41"></div>

          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0369a1] shadow-[0_0_60px_rgba(14,165,233,0.6)] border-2 border-[#0EA5E9]/50 overflow-hidden" data-id="element-42">
            <div className="absolute inset-0 code-flow opacity-30" style={{
            backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255,255,255,0.1) 10px,
                rgba(255,255,255,0.1) 20px
              )`,
            backgroundSize: '200% 200%'
          }} data-id="element-43"></div>

            <div className="absolute inset-4 rounded-full bg-[#0B1120]/40 backdrop-blur-sm flex items-center justify-center" data-id="element-44">
              <Brain className="w-12 h-12 text-white/90" strokeWidth={1.5} data-id="element-45" />
            </div>
          </div>

          <div className="absolute inset-0 rounded-full border border-[#0EA5E9]/30 animate-spin" style={{
          animationDuration: '8s'
        }} data-id="element-46"></div>
          <div className="absolute inset-2 rounded-full border border-[#0EA5E9]/20 animate-spin" style={{
          animationDuration: '6s',
          animationDirection: 'reverse'
        }} data-id="element-47"></div>
        </div>

        <div className="mt-6 text-center" data-id="element-48">
          <p className="text-[#0EA5E9] font-bold text-lg tracking-wider mb-1" data-id="element-49">
            易教智能体
          </p>
          <div className="flex items-center gap-2 justify-center" data-id="element-50">
            <div className="w-2 h-2 rounded-full bg-[#0EA5E9] animate-pulse" data-id="element-51"></div>
            <p className="text-gray-400 text-sm font-mono" data-id="element-52">
              正在运行中 (Running...)
            </p>
          </div>
        </div>

        {photons.map(photon => {
        const offsetFromCenter = photon.targetIndex - 2;
        const cardX = offsetFromCenter * 280;
        const cardY = Math.abs(offsetFromCenter) * 20 + 200;
        return <div key={photon.id} className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full bg-[#0EA5E9] photon pointer-events-none" style={{
          '--photon-x': `${cardX}px`,
          '--photon-y': `${cardY}px`,
          boxShadow: '0 0 10px rgba(14, 165, 233, 0.8)'
        } as React.CSSProperties} data-id="element-53" />;
      })}
      </div>

      {/* Card Ring */}
      <div className="relative w-full flex items-end justify-center perspective-container" data-id="element-54">
        <div className="flex gap-6 items-end justify-center" data-id="element-55">
          {cards.map((card, index) => {
          const offsetFromCenter = index - 2;
          const translateY = Math.abs(offsetFromCenter) * 20;
          const rotateZ = offsetFromCenter * 2;
          return <div key={card.id} style={{
            transform: `translateY(${translateY}px) rotate(${rotateZ}deg)`,
            zIndex: activeIndex === index ? 50 : 10
          }} className="transition-all duration-500" onMouseEnter={() => {
            setActiveIndex(index);
            onManualInteraction();
          }} data-id="element-56">
                <GlassCard {...card} isActive={activeIndex === index} onClick={() => {
              setActiveIndex(index);
              onManualInteraction();
            }} onOpenModule={() => onOpenModule(card.id)} data-id="element-57" />
              </div>;
        })}
        </div>
      </div>
    </div>;
}